import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  getBottomBoundary,
  getLeftBoundary,
  getRightBoundary,
  getShapeChromeScreenRect,
  getTopBoundary,
} from '@/lib/editor/floating-canvas-chrome-bounds'

import { GAP, TH, TW } from '../constants/crop-toolbar-dimensions'

export type CropToolbarPlacement = 'above' | 'below' | 'left' | 'right'

/**
 * Decides where the toolbar will render relative to the shape, using the same fallback order
 * `computeCropToolbarPos` below applies (above, then below, then left, then right). Exported so
 * `CropZoomSlider` can tell whether the toolbar has fallen back to rendering BELOW the shape
 * (not enough room above — e.g. the shape sits near the top of the page) and, if so, stack
 * itself beneath the toolbar instead of directly beneath the shape — they'd otherwise overlap,
 * since both independently default to "just below the shape" when there's room.
 */
export function resolveCropToolbarPlacement(
  shape: ShapeData,
  scale: number,
  canvasRect: DOMRect
): CropToolbarPlacement {
  const { top: st, bottom: sb, left: sl } = getShapeChromeScreenRect(shape, scale, canvasRect)
  const topBoundary = getTopBoundary()
  const bottomBoundary = getBottomBoundary()
  const leftBoundary = getLeftBoundary()

  if (st - TH - GAP >= topBoundary) return 'above'
  if (sb + GAP + TH <= bottomBoundary - 8) return 'below'
  if (sl - TW - GAP >= leftBoundary + 8) return 'left'

  return 'right'
}

export function computeCropToolbarPos(
  shape: ShapeData,
  scale: number,
  canvasRect: DOMRect
): { x: number; y: number } {
  const {
    left: sl,
    top: st,
    right: sr,
    bottom: sb,
  } = getShapeChromeScreenRect(shape, scale, canvasRect)
  const cx = (sl + sr) / 2
  const cy = (st + sb) / 2
  const topBoundary = getTopBoundary()
  const bottomBoundary = getBottomBoundary()

  const leftBoundary = getLeftBoundary()
  const rightBoundary = getRightBoundary()
  const clampX = (v: number) => Math.max(leftBoundary + 8, Math.min(rightBoundary - TW - 8, v))
  const clampY = (v: number) => Math.max(topBoundary, Math.min(bottomBoundary - TH - 8, v))

  const placement = resolveCropToolbarPlacement(shape, scale, canvasRect)

  let x: number
  let y: number

  if (placement === 'above') {
    x = cx - TW / 2
    y = st - TH - GAP
  } else if (placement === 'below') {
    x = cx - TW / 2
    y = sb + GAP
  } else if (placement === 'left') {
    x = sl - TW - GAP
    y = cy - TH / 2
  } else {
    x = sr + GAP
    y = cy - TH / 2
  }

  return { x: clampX(x), y: clampY(y) }
}
